import { EQUIPMENT_PRODUCERS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useEffect, useState } from "react";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import { Box, Typography, Divider, Chip } from "@mui/material";
import { basicInfoContainerStyle } from "../../../../styles/commonStyles";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../../../../components/ui/breadcrumps/breadcrumbsContext";

type DetailRowProps = {
  label: string;
  children: ReactNode;
  noBorder?: boolean;
};

const DetailRow = ({ label, children, noBorder = false }: DetailRowProps) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "200px 1fr" },
      gap: { xs: 0.5, sm: 2 },
      alignItems: "start",
      py: 1.25,
      px: 1.5,
      borderBottom: noBorder ? "none" : "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ minHeight: "24px", display: "flex", alignItems: "center" }}>{children}</Box>
  </Box>
);

interface ProducerModel {
  id: number;
  name: string;
  display_name?: string;
}

interface Producer {
  id: number;
  name: string;
  models?: ProducerModel[];
}

export const ProducerBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(EQUIPMENT_PRODUCERS_ENDPOINT);
  const { setOverride } = useBreadcrumbs();
  const [producer, setProducer] = useState<Producer | null>(null);

  useEffect(() => {
    const fetchProducer = async () => {
      if (id) {
        const response = await get(id);
        if (response?.data) {
          setProducer(response.data);
          setOverride(
            `/equipment-section/producers/${id}`,
            (response.data as Producer).name || id
          );
        }
      }
    };

    const handleProducerUpdated = () => {
      fetchProducer();
    };

    window.addEventListener("equipmentProducerUpdated", handleProducerUpdated);
    fetchProducer();
    return () => {
      window.removeEventListener("equipmentProducerUpdated", handleProducerUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!producer) return <CustomCircularProgress />;

  const models = Array.isArray(producer.models) ? producer.models : [];

  return (
    <Box sx={basicInfoContainerStyle}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {producer.name}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Basic Information")}
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <DetailRow label={t("Name")} noBorder>
            <Typography variant="subtitle1">{producer.name || "—"}</Typography>
          </DetailRow>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {t("Models")}
        </Typography>
        {models.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {models.map((model) => (
              <Chip
                key={model.id}
                label={model.name}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t("No models assigned")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
